import { Link } from 'react-router-dom'

export default function TermsPage() {
  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">Terms of Use</h1>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">Last updated: April 12, 2026</p>

      <div className="prose prose-gray dark:prose-invert max-w-none space-y-6">
        <section>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">1. Acceptance of Terms</h2>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
            By accessing or using StoryBranch ("the Platform"), you agree to be bound by these Terms of Use. If you do not agree to these terms, please do not use the Platform.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">2. Description of Service</h2>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
            StoryBranch is a collaborative storytelling platform that allows users to read, create, and branch stories. The Platform provides tools for content creation, discovery, and community interaction.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">3. User Accounts</h2>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
            You must create an account to access certain features. You are responsible for maintaining the confidentiality of your account credentials and for all activities under your account. You must be at least 13 years of age to use the Platform.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">4. User Content</h2>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
            You retain ownership of original content you create on StoryBranch. By publishing content, you grant StoryBranch a non-exclusive, worldwide, royalty-free license to display, distribute, and promote your content within the Platform. You may not publish content that infringes on others' intellectual property rights.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">5. Branching and Derivative Works</h2>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
            When you create a branch from another user's story, you acknowledge that your branch is a derivative work. The original author retains rights to their content. Branches must respect the original work and comply with any licensing terms set by the original author.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">6. Public Domain Content</h2>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
            StoryBranch may include works sourced from Project Gutenberg and other public domain repositories. These works are in the public domain and are provided as-is. StoryBranch does not claim ownership over public domain content.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">7. Prohibited Conduct</h2>
          <ul className="list-disc pl-6 text-gray-600 dark:text-gray-300 space-y-1">
            <li>Uploading content that is illegal, harmful, threatening, abusive, or hateful</li>
            <li>Impersonating any person or entity</li>
            <li>Attempting to gain unauthorized access to the Platform or other accounts</li>
            <li>Using the Platform to distribute spam or malware</li>
            <li>Scraping or automated data collection without permission</li>
            <li>Circumventing any access controls or payment mechanisms</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">8. Virtual Currency (StoryCoins)</h2>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
            StoryCoins are a virtual currency used within the Platform. StoryCoins can be purchased with real money at a rate of 100 StoryCoins per £1 GBP. Content creators earn StoryCoins when readers purchase their paid branches, subject to a 25% platform commission. Earned StoryCoins can be withdrawn to GBP, with a minimum withdrawal of £10 (1,000 StoryCoins). StoryBranch reserves the right to modify the StoryCoins system, exchange rates, and withdrawal terms at any time.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">9. Content Moderation</h2>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
            StoryBranch reserves the right to remove or restrict content that violates these terms, and to suspend or terminate accounts of users who repeatedly violate community guidelines.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">10. Disclaimer of Warranties</h2>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
            The Platform is provided "as is" and "as available" without warranties of any kind, either express or implied. StoryBranch does not warrant that the service will be uninterrupted, secure, or error-free.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">11. Limitation of Liability</h2>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
            To the fullest extent permitted by law, StoryBranch shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Platform.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">12. Changes to Terms</h2>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
            StoryBranch reserves the right to modify these terms at any time. We will notify users of material changes via the Platform. Continued use after changes constitutes acceptance of the updated terms.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">13. Contact</h2>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
            For questions about these Terms of Use, please contact us at <a href="mailto:support@storybranch.app" className="text-primary-600 dark:text-primary-400 hover:underline">support@storybranch.app</a>.
          </p>
        </section>
      </div>

      <div className="mt-10 pt-6 border-t border-gray-200 dark:border-gray-700">
        <Link to="/privacy" className="text-primary-600 dark:text-primary-400 hover:underline text-sm">
          Privacy Policy →
        </Link>
      </div>
    </div>
  )
}
